import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, X, Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import API from '../api/client'
import { useI18n, type Lang, type Translations } from '../i18n/translations'
import { useSubmitAccessRequest } from '../hooks/useAccessRequests'

type Role = 'expediteur' | 'livreur' | 'admin'

interface LoginPayload { email: string; password: string }

// ─── Design tokens ────────────────────────────────────────
import '../styles/landing.css'

const CSS = `
  /* styles spécifiques Landing uniquement */
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes pulse { 0% { transform: scale(.6); opacity: .5; } 100% { transform: scale(2); opacity: 0; } }
`

// ─── Preview cards ─────────────────────────────────────────
const PreviewExpediteur = ({ t }: { t: Translations['preview'] }) => (
  <div className="preview-body">
    <div className="pv-stat-row">
      <div className="pv-stat"><small>{t.thisMonth}</small><b>148</b><span>▲ 12%</span></div>
      <div className="pv-stat"><small>{t.inProgress}</small><b>23</b><span>{t.delivered}</span></div>
      <div className="pv-stat"><small>{t.revenue}</small><b>4,2K</b><span>TND</span></div>
    </div>
    <div className="pv-list">
      {[
        { id: 'TQ-48201', to: 'Karim Hajri', city: 'Sfax', status: 'delivered', amount: '84 TND' },
        { id: 'TQ-48198', to: 'Yasmine B.', city: 'Sousse', status: 'transit', amount: '62 TND' },
        { id: 'TQ-48195', to: 'Ali Mzoughi', city: 'Nabeul', status: 'pickup', amount: '40 TND' },
      ].map(r => (
        <div className="pv-row" key={r.id}>
          <span className="pv-id">{r.id}</span>
          <div className="pv-to"><b>{r.to}</b><span>{r.city}</span></div>
          <span className={`pv-status pv-${r.status}`}>
            {r.status === 'delivered' ? t.delivered : r.status === 'transit' ? t.inTransit : t.pickup}
          </span>
          <span className="pv-amount">{r.amount}</span>
        </div>
      ))}
    </div>
  </div>
)

const PreviewLivreur = ({ t }: { t: Translations['preview'] }) => (
  <div className="preview-body">

    {/* ── Mini-map trajet A → B ── */}
    <div style={{
      borderRadius: 14,
      border: '1px solid var(--line)',
      overflow: 'hidden',
      marginBottom: 14,
      background: '#F4F7F2',
      position: 'relative',
      height: 130,
    }}>
      <svg
        viewBox="0 0 340 130"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <defs>
          <pattern id="grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(15,42,74,.07)" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="340" height="130" fill="url(#grid)" />

        {/* Ligne pointillée A → B */}
        <path
          d="M 40 95 C 90 95 100 40 160 42 C 220 44 240 42 300 30"
          fill="none"
          stroke="#7CB342"
          strokeWidth="2.5"
          strokeDasharray="6 5"
          strokeLinecap="round"
        />

        {/* Ligne animée par-dessus */}
        <path
          d="M 40 95 C 90 95 100 40 160 42 C 220 44 240 42 300 30"
          fill="none"
          stroke="#7CB342"
          strokeWidth="2.5"
          strokeDasharray="40 260"
          strokeLinecap="round"
          style={{ animation: 'trailMove 3s linear infinite' }}
        />

        {/* Point A — Pickup (vert) */}
        <circle cx="40" cy="95" r="7" fill="#fff" stroke="#7CB342" strokeWidth="2.5" />
        <circle cx="40" cy="95" r="3.5" fill="#7CB342" />

        {/* Point B — Drop (orange) */}
        <circle cx="300" cy="30" r="7" fill="#fff" stroke="#F3A845" strokeWidth="2.5" />
        <circle cx="300" cy="30" r="3.5" fill="#F3A845" />

        {/* Livreur en mouvement */}
        <circle r="9" fill="#0A1E3A" stroke="#fff" strokeWidth="2">
          <animateMotion
            dur="3s"
            repeatCount="indefinite"
            path="M 40 95 C 90 95 100 40 160 42 C 220 44 240 42 300 30"
          />
        </circle>
        <text fontSize="9" textAnchor="middle" fill="#fff" dy="3">🛵</text>

        <style>{`
          @keyframes trailMove {
            0%   { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -300; }
          }
        `}</style>
      </svg>

      {/* Badge "Driver app" */}
      <div style={{
        position: 'absolute', top: 10, right: 10,
        background: 'rgba(10,30,58,.75)', backdropFilter: 'blur(6px)',
        color: '#7CB342', fontSize: '.68rem', fontWeight: 700,
        padding: '4px 10px', borderRadius: 99,
        letterSpacing: '.06em', textTransform: 'uppercase',
      }}>
        Driver app
      </div>

      {/* Labels A et B */}
      <div style={{
        position: 'absolute', bottom: 8, left: 10,
        display: 'flex', gap: 10, fontSize: '.68rem', fontWeight: 600,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#5d9933' }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: '#7CB342', display: 'inline-block' }} />
          Pickup
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#a06d12' }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: '#F3A845', display: 'inline-block' }} />
          Drop
        </span>
      </div>
    </div>

    {/* ── Earnings ── */}
    <div className="pv-earnings">
      <div>
        <small>{t.today}</small>
        <b>84.500 TND</b>
      </div>
      <span className="pv-trend">▲ 23%</span>
    </div>

    {/* ── Commande ── */}
    <div className="pv-order">
      <div className="pv-order-head">
        <b>#TQ-48201</b>
        <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>4.2 km · 18 min</span>
      </div>
      <div style={{ fontSize: '.85rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: '#7CB342', flexShrink: 0 }} />
          <div>
            <b style={{ color: 'var(--navy-900)', display: 'block' }}>Av. Habib Bourguiba</b>
            <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>Pickup · Tunis Centre</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: '#F3A845', flexShrink: 0 }} />
          <div>
            <b style={{ color: 'var(--navy-900)', display: 'block' }}>Rue de la Liberté</b>
            <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>Drop · La Marsa</span>
          </div>
        </div>
      </div>
      <div className="pv-order-cta">{t.accept}</div>
    </div>
  </div>
)

const PreviewAdmin = ({ t }: { t: Translations['preview'] }) => (
  <div className="preview-body">
    <div className="pv-kpis">
      <div className="pv-kpi"><small>{t.deliveries}</small><b>186</b></div>
      <div className="pv-kpi"><small>{t.online}</small><b>438</b></div>
      <div className="pv-kpi"><small>{t.sla}</small><b>96%</b></div>
      <div className="pv-kpi"><small>{t.delays}</small><b style={{ color: '#F3A845' }}>3</b></div>
    </div>
    <div className="pv-chart">
      {[60, 78, 45, 88, 72, 95, 64, 80, 55, 90].map((h, i) => (
        <i key={i} style={{ height: `${h}%` }} />
      ))}
    </div>
    <div className="pv-feed">
      {[
        { msg: 'TQ-48201 ' + t.delivered, time: '09:41', warn: false },
        { msg: 'TQ-48198 SLA risk',        time: '09:38', warn: true },
        { msg: 'Youssef K. ' + t.online,   time: '09:35', warn: false },
      ].map((f, i) => (
        <div className="pv-feed-row" key={i}>
          <span className={`feed-dot${f.warn ? ' warn' : ''}`} />
          <b>{f.msg}</b>
          <span>{f.time}</span>
        </div>
      ))}
    </div>
  </div>
)

// ─── Tunisia Map ──────────────────────────────────────────
interface TunisiaMapProps {
  toastMessages: { title: string; sub: string }[]
  toastIdx: number
  toastVisible: boolean
  lang: Lang
}

const TunisiaMap = ({ toastMessages, toastIdx, toastVisible }: TunisiaMapProps) => {
  const cities: { name: string; x: number; y: number; size?: 'lg' | 'sm' }[] = [
    { name: 'Tunis',      x: 258, y: 85,  size: 'lg' },
    { name: 'Nabeul',    x: 295, y: 128, size: 'sm' },
    { name: 'Béja',      x: 185, y: 118, size: 'sm' },
    { name: 'Le Kef',    x: 155, y: 175, size: 'sm' },
    { name: 'Sousse',    x: 272, y: 220, size: 'sm' },
    { name: 'Kairouan',  x: 230, y: 248, size: 'sm' },
    { name: 'Monastir',  x: 285, y: 248, size: 'sm' },
    { name: 'Mahdia',    x: 285, y: 280, size: 'sm' },
    { name: 'Kasserine', x: 185, y: 290, size: 'sm' },
    { name: 'Sfax',      x: 272, y: 340, size: 'lg' },
    { name: 'Gafsa',     x: 175, y: 360, size: 'sm' },
    { name: 'Gabès',     x: 270, y: 405, size: 'sm' },
    { name: 'Médenine',  x: 268, y: 460, size: 'sm' },
    { name: 'Djerba',    x: 298, y: 470, size: 'sm' },
    { name: 'Tataouine', x: 248, y: 510, size: 'sm' },
    { name: 'Tozeur',    x: 158, y: 415, size: 'sm' },
  ]

  const routes: [string, string][] = [
    ['Tunis',    'Nabeul'],
    ['Tunis',    'Sousse'],
    ['Tunis',    'Sfax'],
    ['Sousse',   'Sfax'],
    ['Sfax',     'Gabès'],
    ['Gabès',    'Médenine'],
    ['Médenine', 'Djerba'],
    ['Médenine', 'Tataouine'],
  ]

  const getCity = (name: string) => cities.find(c => c.name === name)!
  const statusColors = ['#7CB342', '#4FA0E8', '#F3A845']

  const tunisiaPath = `
    M 258 60 C 270 62 280 68 285 80 C 295 90 300 110 296 128
    C 298 140 295 155 290 165 C 295 180 290 200 285 215
    C 288 230 285 248 282 265 C 285 280 285 295 280 310
    C 278 325 275 335 272 345 C 275 360 272 375 270 390
    C 272 405 268 420 265 435 C 268 448 265 460 260 470
    C 265 480 268 492 260 505 C 255 515 248 520 240 520
    C 225 520 215 515 205 508 C 190 495 178 480 170 465
    C 160 448 152 430 148 412 C 145 398 148 383 150 368
    C 148 352 145 338 140 325 C 135 308 130 292 128 275
    C 125 258 128 242 132 226 C 135 210 140 195 145 180
    C 140 165 138 148 140 132 C 138 115 142 98 148 83
    C 155 68 165 58 178 52 C 195 45 215 42 235 46
    C 245 48 253 55 258 60 Z
  `

  return (
    <div style={{
      position: 'relative',
      background: 'linear-gradient(160deg, #0F2A4A 0%, #06152C 100%)',
      border: '1px solid rgba(255,255,255,.08)',
      borderRadius: 24,
      overflow: 'hidden',
      boxShadow: '0 40px 80px -30px rgba(0,0,0,.6)',
      minHeight: 560,
    }}>
      {/* Toast */}
      <div style={{
        position: 'absolute', top: 16, left: 16, right: 16, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(10,30,58,.9)', backdropFilter: 'blur(14px)',
        border: '1px solid rgba(124,179,66,.25)', borderRadius: 14,
        padding: '12px 16px',
        transition: 'opacity .4s, transform .4s',
        opacity: toastVisible ? 1 : 0,
        transform: toastVisible ? 'none' : 'translateY(-8px)',
      }}>
        <span style={{
          width: 10, height: 10, borderRadius: 99, background: '#7CB342',
          display: 'inline-block', flexShrink: 0,
          boxShadow: '0 0 8px #7CB342',
          animation: 'pulse 1.8s ease-out infinite',
        }} />
        <div>
          <b style={{ display: 'block', color: '#fff', fontSize: '.92rem' }}>
            {toastMessages[toastIdx].title}
          </b>
          <span style={{ color: 'rgba(255,255,255,.6)', fontSize: '.78rem', fontFamily: 'JetBrains Mono' }}>
            {toastMessages[toastIdx].sub}
          </span>
        </div>
      </div>

      {/* SVG Map */}
      <svg viewBox="0 0 400 580" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', minHeight: 560 }}>
        <defs>
          <pattern id="mapgrid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="rgba(255,255,255,.04)" strokeWidth="1"/>
          </pattern>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <rect width="400" height="580" fill="url(#mapgrid)" />

        {/* Silhouette Tunisie */}
        <path d={tunisiaPath} fill="rgba(124,179,66,.04)" />
        <path d={tunisiaPath} fill="none" stroke="#7CB342" strokeWidth="1.5" strokeDasharray="8 6" opacity=".5" />

        {/* Routes */}
        {routes.map(([a, b]) => {
          const ca = getCity(a)
          const cb = getCity(b)
          const dist = Math.hypot(cb.x - ca.x, cb.y - ca.y)
          return (
            <g key={`${a}-${b}`}>
              <line x1={ca.x} y1={ca.y} x2={cb.x} y2={cb.y} stroke="rgba(255,255,255,.08)" strokeWidth="3" />
              <line
                x1={ca.x} y1={ca.y} x2={cb.x} y2={cb.y}
                stroke="#fff" strokeWidth="2.5" strokeLinecap="round"
                strokeDasharray={`${dist * 0.35} ${dist}`}
                style={{ animation: `routeFlow ${2 + (dist % 2)}s linear infinite`, animationDelay: `${(dist % 200) / 100}s` }}
              />
            </g>
          )
        })}

        {/* Villes */}
        {cities.map((city, i) => {
          const isLg = city.size === 'lg'
          const r = isLg ? 8 : 5
          const color = statusColors[i % statusColors.length]
          return (
            <g key={city.name} filter={isLg ? 'url(#glow)' : undefined}>
              {isLg && <circle cx={city.x} cy={city.y} r={r + 8} fill={color} opacity=".12" />}
              <circle cx={city.x} cy={city.y} r={r} fill="#0F2A4A" stroke="#fff" strokeWidth={isLg ? 2 : 1.5} />
              <circle cx={city.x} cy={city.y} r={isLg ? 4 : 2.5} fill={isLg ? '#7CB342' : color} />
              <text
                x={city.x + (city.x > 250 ? r + 5 : -(r + 5))}
                y={city.y + 4}
                fill="rgba(255,255,255,.75)"
                fontSize={isLg ? 11 : 9.5}
                fontFamily="JetBrains Mono"
                fontWeight={isLg ? '700' : '400'}
                textAnchor={city.x > 250 ? 'start' : 'end'}
              >
                {city.name}
              </text>
            </g>
          )
        })}

        {/* Livreur 1 : Tunis → Sousse → Sfax */}
        <circle r="7" fill="#7CB342" stroke="#fff" strokeWidth="2" filter="url(#glow)">
          <animateMotion dur="6s" repeatCount="indefinite"
            path={`M ${getCity('Tunis').x} ${getCity('Tunis').y} L ${getCity('Sousse').x} ${getCity('Sousse').y} L ${getCity('Sfax').x} ${getCity('Sfax').y}`}
          />
        </circle>

        {/* Livreur 2 : Sfax → Gabès */}
        <circle r="6" fill="#4FA0E8" stroke="#fff" strokeWidth="1.5">
          <animateMotion dur="4s" repeatCount="indefinite" begin="2s"
            path={`M ${getCity('Sfax').x} ${getCity('Sfax').y} L ${getCity('Gabès').x} ${getCity('Gabès').y}`}
          />
        </circle>

        {/* Livreur 3 : Tunis → Nabeul */}
        <circle r="5" fill="#F3A845" stroke="#fff" strokeWidth="1.5">
          <animateMotion dur="3s" repeatCount="indefinite" begin="1s"
            path={`M ${getCity('Tunis').x} ${getCity('Tunis').y} L ${getCity('Nabeul').x} ${getCity('Nabeul').y}`}
          />
        </circle>

        <style>{`@keyframes routeFlow { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -400; } }`}</style>
      </svg>
    </div>
  )
}

// ─── Lang options ──────────────────────────────────────────
const LANGS: { code: Lang; flag: string; name: string; native: string }[] = [
  { code: 'fr', flag: '🇫🇷', name: 'Français', native: 'FR' },
  { code: 'en', flag: '🇬🇧', name: 'English',  native: 'EN' },
  { code: 'ar', flag: '🇹🇳', name: 'العربية',  native: 'AR' },
]

// ─── Main component ────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate()
  const { user, isAuthenticated, setUser } = useAuthStore()
  const { lang, t, changeLang } = useI18n()

  const handleGoDashboard = () => {
    if (!user) return
    const roleRoutes: Record<string, string> = { admin: '/admin', expediteur: '/expediteur', livreur: '/livreur' }
    navigate(roleRoutes[user.role] ?? '/dashboard')
  }

  const [activeRole, setActiveRole] = useState<Role>('expediteur')
  const [showModal, setShowModal] = useState(false)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [formRole, setFormRole] = useState<Role>('expediteur')
  const { submit: submitRequest, loading: formLoading, success: formSuccess, error: formError } = useSubmitAccessRequest()
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '',
    volume: t.contact.form.volumes[0] ?? '',
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastIdx, setToastIdx] = useState(0)
  const [statsStarted, setStatsStarted] = useState(false)
  const [counts, setCounts] = useState({ deliveries: 0, couriers: 0, businesses: 0 })
  const statsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const show = setInterval(() => {
      setToastVisible(true)
      setTimeout(() => {
        setToastVisible(false)
        setTimeout(() => setToastIdx(i => (i + 1) % 3), 500)
      }, 3000)
    }, 5000)
    setTimeout(() => setToastVisible(true), 1000)
    return () => clearInterval(show)
  }, [])

  const animateCount = useCallback((target: number, key: keyof typeof counts, duration = 1800) => {
    const start = Date.now()
    const tick = () => {
      const elapsed = Date.now() - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCounts(prev => ({ ...prev, [key]: Math.round(target * eased) }))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [])

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !statsStarted) {
        setStatsStarted(true)
        animateCount(186420, 'deliveries')
        animateCount(2340, 'couriers')
        animateCount(1280, 'businesses')
      }
    }, { threshold: 0.3 })
    if (statsRef.current) obs.observe(statsRef.current)
    return () => obs.disconnect()
  }, [statsStarted, animateCount])

  const closeModal = () => {
    setShowModal(false)
    setLoginEmail('')
    setLoginPassword('')
    setLoginError('')
    setShowPassword(false)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    try {
      const { data } = await API.post('/auth/login-email', { email: loginEmail, password: loginPassword } as LoginPayload)
      setUser(data.user, data.accessToken)
      closeModal()
      const roleRoutes: Record<string, string> = { admin: '/admin', expediteur: '/expediteur', livreur: '/livreur' }
      navigate(roleRoutes[data.user.role] ?? '/dashboard')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setLoginError(msg ?? (lang === 'ar' ? 'البريد الإلكتروني أو كلمة المرور غير صحيحة' : lang === 'en' ? 'Invalid email or password' : 'Email ou mot de passe incorrect'))
    } finally {
      setLoginLoading(false)
    }
  }

  const styleRef = useRef<HTMLStyleElement | null>(null)
  useEffect(() => {
    if (!styleRef.current) {
      const el = document.createElement('style')
      el.textContent = CSS
      document.head.appendChild(el)
      styleRef.current = el
    }
    return () => { if (styleRef.current) { document.head.removeChild(styleRef.current); styleRef.current = null } }
  }, [])

  const arrow = lang === 'ar' ? '←' : '→'

  const toastMessages = lang === 'ar'
    ? [
        { title: 'تم تسليم الطرد', sub: 'TQ-48091 · تونس → سوسة' },
        { title: 'تم تعيين المندوب', sub: 'TQ-48102 · يوسف ك.' },
        { title: 'تم تأكيد الدفع', sub: 'TQ-48087 · 84 دينار مستلم' },
      ]
    : lang === 'fr'
      ? [
          { title: 'Colis livré', sub: 'TQ-48091 · Tunis → Sousse' },
          { title: 'Livreur assigné', sub: 'TQ-48102 · Youssef K.' },
          { title: 'Paiement confirmé', sub: 'TQ-48087 · 84 TND reçu' },
        ]
      : [
          { title: 'Package delivered', sub: 'TQ-48091 · Tunis → Sousse' },
          { title: 'Driver assigned', sub: 'TQ-48102 · Youssef K.' },
          { title: 'Payment confirmed', sub: 'TQ-48087 · 84 TND received' },
        ]

  // ─── Phone Mockup ────────────────────────────────────────
  const PhoneMockup = () => (
    <div className="dl-phone-wrap">
      <div className="dl-phone">
        <div className="dl-phone-notch" />
        <div className="dl-phone-screen">
          <div className="dl-screen-status">
            <span>09:41</span>
            <span>📶 🔋 100%</span>
          </div>
          <div className="dl-screen-greeting">
            <b>Bonjour Youssef</b>
            <small>Tuesday, 16 May</small>
          </div>
          <div className="dl-screen-earnings">
            <small>TODAY'S EARNINGS</small>
            <div className="dl-screen-earnings-row">
              <b>84.500 TND</b>
              <span className="dl-screen-trend">▲ 23%</span>
            </div>
          </div>
          <div className="dl-screen-order-label">NEXT ORDER · 4.2 KM</div>
          <div className="dl-screen-order">
            <div className="dl-screen-order-head">
              <b>#TQ-48201</b>
              <span className="dl-screen-order-eta">18 min</span>
            </div>
            <div className="dl-screen-stop">
              <span className="dl-screen-stop-dot" style={{ background: '#7CB342' }} />
              <div>
                <b>Av. Habib Bourguiba</b>
                <span>Pickup · Tunis Centre</span>
              </div>
            </div>
            <div style={{ width: 1, height: 10, background: 'var(--line)', margin: '0 3.5px 6px' }} />
            <div className="dl-screen-stop">
              <span className="dl-screen-stop-dot" style={{ background: '#F3A845' }} />
              <div>
                <b>Rue de la Liberté</b>
                <span>Drop · La Marsa</span>
              </div>
            </div>
            <div className="dl-screen-accept">Accept order →</div>
          </div>
          <div className="dl-screen-tabs">
            <span className="dl-screen-tab active">Home</span>
            <span className="dl-screen-tab">Orders</span>
            <span className="dl-screen-tab">Earnings</span>
            <span className="dl-screen-tab">Profile</span>
          </div>
        </div>
      </div>
      <div style={{
        position: 'absolute', bottom: -60, left: '50%', transform: 'translateX(-50%)',
        width: 200, height: 100, borderRadius: '50%',
        background: 'rgba(124,179,66,.25)', filter: 'blur(40px)', zIndex: 0,
        pointerEvents: 'none',
      }} />
    </div>
  )

  // ─── LangSwitcher ───────────────────────────────────────
  const LangSwitcher = () => (
    <div style={{ position: 'relative' }}>
      <button
        className="lang-btn"
        onClick={() => setShowLangMenu(v => !v)}
        aria-label="Change language"
      >
        <span className="lang-dot" />
        {lang.toUpperCase()}
        <span style={{ fontSize: '.65rem', opacity: .5 }}>▼</span>
      </button>
      {showLangMenu && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            onClick={() => setShowLangMenu(false)}
          />
          <div className="lang-menu">
            {LANGS.map(l => (
              <button
                key={l.code}
                className={`lang-option${lang === l.code ? ' active' : ''}`}
                onClick={() => { changeLang(l.code); setShowLangMenu(false) }}
              >
                <span className="flag">{l.flag}</span>
                <span className="lname">{l.name}</span>
                <span className="native">{l.native}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      {/* ═══ NAV ═══ */}
      <header className={`nav${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">
          {/* Logo */}
          <a href="#" className="nav-logo" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src="/assets/logo.png" alt="TraiqGo" style={{ height: 48 }} />
          </a>

          {/* Liens desktop */}
          <nav className="nav-links">
            <a href="#how">{t.nav.how}</a>
            <a href="#map">{t.nav.map}</a>
            <a href="#testimonials">{t.nav.testimonials}</a>
            <a href="#contact">{t.nav.contact}</a>
          </nav>

          {/* Actions — Sign in centré sur mobile entre logo et toggle */}
          <div className="nav-actions" style={{ marginLeft: 'auto' }}>
            <div className="nav-lang-btn">
              <LangSwitcher />
            </div>
            {isAuthenticated && user ? (
              <button className="btn btn-ghost" onClick={handleGoDashboard}>
                {user.name}
              </button>
            ) : (
              <button className="btn btn-ghost" onClick={() => setShowModal(true)}>{t.nav.login}</button>
            )}
            <a href="#contact" className="btn btn-primary nav-desktop-cta">{t.nav.cta} <span>{arrow}</span></a>
          </div>

          {/* Bouton hamburger — visible uniquement sur mobile */}
          <button
            className="nav-toggle"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Drawer mobile */}
        <div className={`nav-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
          <a className="nav-mobile-link" href="#how" onClick={() => setMobileMenuOpen(false)}>{t.nav.how}</a>
          <a className="nav-mobile-link" href="#map" onClick={() => setMobileMenuOpen(false)}>{t.nav.map}</a>
          <a className="nav-mobile-link" href="#testimonials" onClick={() => setMobileMenuOpen(false)}>{t.nav.testimonials}</a>
          <a className="nav-mobile-link" href="#contact" onClick={() => setMobileMenuOpen(false)}>{t.nav.contact}</a>
          <div className="nav-mobile-divider" />
          <button className="nav-mobile-cta" onClick={() => { setMobileMenuOpen(false); window.location.hash = 'contact' }}>
            {t.nav.cta} {arrow}
          </button>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="hero">
        <div className="hero-bg">
          <div className="grid-lines" />
          <div className="blob blob-green" />
          <div className="blob blob-navy" />
        </div>
        <div className="hero-inner">
          <div>
            <div className="eyebrow">
              <span className="pulse-dot" />
              {t.hero.eyebrow}
            </div>
            <h1 className="hero-title">
              {t.hero.titlePre}{' '}
              <span className={`role-word${activeRole === 'expediteur' ? ' active' : ''}`}>{t.hero.role.expediteur}</span>{t.hero.titleMid}<br />
              <span className={`role-word${activeRole === 'livreur' ? ' active' : ''}`}>{t.hero.role.livreur}</span><br />
              {t.hero.titleAnd}{' '}
              <span className={`role-word${activeRole === 'admin' ? ' active' : ''}`}>{t.hero.role.admin}</span>{t.hero.titlePost}
            </h1>
            <p className="hero-sub">{t.hero.sub[activeRole]}</p>

            <div className="role-tabs">
              {([
                { role: 'expediteur', icon: '📦' },
                { role: 'livreur',    icon: '🛵' },
                { role: 'admin',      icon: '⚙️' },
              ] as { role: Role; icon: string }[]).map(tab => (
                <button
                  key={tab.role}
                  className={`role-tab${activeRole === tab.role ? ' active' : ''}`}
                  onClick={() => setActiveRole(tab.role)}
                >
                  <span className="ri">{tab.icon}</span>
                  <span className="rn">{t.hero.role[tab.role]}</span>
                  <span className="rs">{tab.role === 'expediteur' ? 'Shipper' : tab.role === 'livreur' ? 'Courier' : 'Operations'}</span>
                </button>
              ))}
            </div>

            <div className="hero-ctas">
              <a href="#contact" className="btn btn-primary btn-lg">
                {t.hero.cta[activeRole]} <span>{arrow}</span>
              </a>
              <a href="#how" className="btn btn-ghost btn-lg">
                ▶ {t.hero.howBtn}
              </a>
            </div>

            <div className="hero-trust">
              <div className="avatars">
                <span style={{ background: '#0F2A4A' }}>SM</span>
                <span style={{ background: '#7CB342' }}>KB</span>
                <span style={{ background: '#1E3A5F' }}>YA</span>
                <span style={{ background: '#5d9933' }}>+</span>
              </div>
              <div>
                <strong>1 200+</strong> {t.hero.trust}
              </div>
            </div>
          </div>

          <div className="hero-preview">
            <div className="preview-head">
              <div className="ph-dots"><i /><i /><i /></div>
              <span className="ph-title">traiqgo.tn/dashboard</span>
              <span className="ph-badge">● Live</span>
            </div>
            {activeRole === 'expediteur' && <PreviewExpediteur t={t.preview} />}
            {activeRole === 'livreur'    && <PreviewLivreur t={t.preview} />}
            {activeRole === 'admin'      && <PreviewAdmin t={t.preview} />}
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="stats" ref={statsRef}>
        <div className="stats-inner">
          <div className="stat">
            <div className="stat-num">{counts.deliveries.toLocaleString()}</div>
            <div className="stat-label">{t.stats.deliveries.label}</div>
            <div className="stat-trend">{t.stats.deliveries.trend}</div>
          </div>
          <div className="stat">
            <div className="stat-num">{counts.couriers.toLocaleString()}</div>
            <div className="stat-label">{t.stats.couriers.label}</div>
            <div className="stat-trend">{t.stats.couriers.trend}</div>
          </div>
          <div className="stat">
            <div className="stat-num">{counts.businesses.toLocaleString()}</div>
            <div className="stat-label">{t.stats.businesses.label}</div>
            <div className="stat-trend">{t.stats.businesses.trend}</div>
          </div>
          <div className="stat">
            <div className="stat-num">24/7</div>
            <div className="stat-label">{t.stats.ops.label}</div>
            <div className="stat-trend">{t.stats.ops.trend}</div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="how" id="how">
        <div className="section-head">
          <div className="kicker">{t.how.kicker}</div>
          <h2>{t.how.title}</h2>
          <p>{t.how.sub}</p>
        </div>
        <div className="steps">
          {[
            { step: t.how.step1, num: '01' },
            { step: t.how.step2, num: '02' },
            { step: t.how.step3, num: '03' },
          ].map(({ step, num }) => (
            <div className="step" key={num}>
              <div className="step-header">
                <span className="step-num">{num}</span>
                <span className="step-tag">{step.tag}</span>
              </div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
              {num === '01' && (
                <div className="step-visual">
                  <div className="mini-input"><span>{lang === 'ar' ? 'من' : 'De'}</span><b>Av. Habib Bourguiba, Tunis</b></div>
                  <div className="mini-input"><span>{lang === 'ar' ? 'إلى' : 'À'}</span><b>Rue de la Liberté, Sfax</b></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span className="chip">{lang === 'ar' ? 'متوسط · 3 كغ' : 'Moyen · 3 kg'}</span>
                    <span className="chip chip-green">COD 84 TND</span>
                  </div>
                </div>
              )}
              {num === '02' && (
                <div className="step-visual">
                  <div style={{ background: '#fff', borderRadius: 14, padding: 14, border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--green-700)', fontWeight: 600, marginBottom: 8 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--green-500)', display: 'inline-block', animation: 'pulse 1.8s infinite' }} />
                      {t.preview.newOrder}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <b style={{ fontFamily: 'JetBrains Mono', fontSize: '.95rem' }}>#TQ-48201</b>
                      <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>18 min</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 8, background: 'var(--navy-50)', color: 'var(--navy-700)', fontWeight: 600, fontSize: '.82rem' }}>{t.preview.decline}</span>
                      <span style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 8, background: 'var(--green-500)', color: '#fff', fontWeight: 700, fontSize: '.82rem' }}>{t.preview.accept}</span>
                    </div>
                  </div>
                </div>
              )}
              {num === '03' && (
                <div className="step-visual">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 10 }}>
                    {[
                      [lang === 'ar' ? 'في الوقت' : 'À temps', '96,4%'],
                      [lang === 'ar' ? 'خطر SLA' : 'Risque SLA', '3'],
                      [lang === 'ar' ? 'مدفوعات' : 'Paiements', 'OK'],
                    ].map(([l, v]) => (
                      <div key={l} style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 10, padding: 9, textAlign: 'center' }}>
                        <span style={{ display: 'block', fontSize: '.68rem', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '.05em', marginBottom: 2 }}>{l}</span>
                        <b style={{ fontFamily: 'Space Grotesk', fontSize: '.95rem', color: v === '3' ? '#F3A845' : 'var(--navy-900)' }}>{v}</b>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 60 }}>
                    {[60, 78, 45, 88, 72, 95, 64].map((h, i) => (
                      <div key={i} style={{ flex: 1, height: `${h}%`, background: 'linear-gradient(180deg, var(--green-400), var(--green-600))', borderRadius: '3px 3px 0 0' }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ LIVE TRACKING ═══ */}
      <section className="map-section" id="map">
        <div className="map-grid">

          {/* ── Copie gauche ── */}
          <div className="map-copy">
            <div className="kicker" style={{ background: 'rgba(124,179,66,.18)', color: '#7CB342' }}>
              {lang === 'ar' ? 'تتبع مباشر' : lang === 'fr' ? 'Tracking live' : 'Live tracking'}
            </div>
            <h2>
              {lang === 'ar'
                ? 'كل طرد، على الخريطة، في الوقت الفعلي.'
                : lang === 'fr'
                  ? 'Chaque colis, sur la carte, en temps réel.'
                  : 'Every package, on the map, in real time.'}
            </h2>
            <p style={{ marginBottom: 28 }}>
              {lang === 'ar'
                ? 'شاهد المندوبين وهم يتنقلون عبر البلاد. المرسلون يتابعون طلباتهم دون اتصال. المشرفون يرصدون التأخيرات قبل أن تصبح مشاكل.'
                : lang === 'fr'
                  ? "Observez les livreurs traverser le pays. Les expéditeurs suivent leur commande sans appeler. Les admins repèrent les retards avant qu'ils ne deviennent des problèmes."
                  : 'Watch couriers move across the country. Shippers see their order without calling. Admins spot delays before they become problems.'}
            </p>

            <ul className="map-bullets" style={{ marginBottom: 28 }}>
              {(lang === 'ar'
                ? [
                    ['b-green',  'GPS كل 4 ثوانٍ، محسَّن لاستهلاك البطارية'],
                    ['b-amber',  'تأكيدات الاستلام والتسليم بالسياج الجغرافي'],
                    ['b-blue',   'إعادة حساب وقت الوصول مع حركة المرور والبيانات التاريخية'],
                    ['b-purple', 'رابط تتبع قابل للمشاركة — لا تطبيق للعميل النهائي'],
                  ]
                : lang === 'fr'
                  ? [
                      ['b-green',  'GPS toutes les 4 secondes, optimisé pour la batterie'],
                      ['b-amber',  'Confirmation de pickup & livraison géofencée'],
                      ['b-blue',   'ETA recalculé avec trafic et données historiques'],
                      ['b-purple', 'Lien de tracking partageable — sans application pour le client final'],
                    ]
                  : [
                      ['b-green',  'GPS pings every 4 seconds, batched for battery life'],
                      ['b-amber',  'Geofenced pickup & drop confirmations'],
                      ['b-blue',   'ETA recalculated with traffic and historical data'],
                      ['b-purple', 'Shareable tracking link — no app required for end customers'],
                    ]
              ).map(([cls, txt]) => (
                <li key={cls}>
                  <span className={`bullet ${cls}`} />
                  {txt}
                </li>
              ))}
            </ul>

            {/* Légende statuts */}
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {[
                { dot: '#7CB342', label: lang === 'ar' ? 'مُسلَّم'    : lang === 'fr' ? 'Livré'      : 'Delivered' },
                { dot: '#4FA0E8', label: lang === 'ar' ? 'في الطريق'  : lang === 'fr' ? 'En transit' : 'In transit' },
                { dot: '#F3A845', label: lang === 'ar' ? 'عند الاستلام': lang === 'fr' ? 'Au pickup'  : 'At pickup' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', color: 'rgba(255,255,255,.75)' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 99, background: s.dot, display: 'inline-block', boxShadow: `0 0 6px ${s.dot}` }} />
                  {s.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Carte Tunisie ── */}
          <TunisiaMap toastMessages={toastMessages} toastIdx={toastIdx} toastVisible={toastVisible} lang={lang} />

        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="testimonials" id="testimonials">
        <div className="section-head">
          <div className="kicker">{t.t.kicker}</div>
          <h2>{t.t.title}</h2>
        </div>
        <div className="t-grid">
          {t.t.cards.map((card, i) => (
            <article key={i} className={`t-card${i === 1 ? ' t-card-feature' : ''}`}>
              <div className="t-quote">{card.quote}</div>
              <div className="t-meta">
                <div className="t-avatar" style={{ background: ['#0F2A4A', '#7CB342', '#1E3A5F'][i] }}>
                  {card.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div><b>{card.name}</b><span>{card.role_}</span></div>
                <span className={`t-role${i === 1 ? ' t-role-green' : ''}`}>{card.tag}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ═══ DOWNLOAD APP ═══ */}
      <section className="download">
        <div className="download-inner">

          {/* Copie gauche */}
          <div className="dl-copy">
            <div className="dl-kicker">
              {lang === 'ar' ? 'للمندوبين على الطريق' : lang === 'fr' ? 'Pour les livreurs sur la route' : 'For couriers on the road'}
            </div>
            <h2>
              {lang === 'ar'
                ? 'تطبيق المندوب، مصمم ليد واحدة وخوذة.'
                : lang === 'fr'
                  ? "L'app Livreur, conçue pour une main et un casque."
                  : 'The Livreur app, built for one hand and a helmet.'}
            </h2>
            <p>
              {lang === 'ar'
                ? 'اقبل الطلبات، تنقّل، امسح الباركود، اجمع الأموال، استلم أجرك. لا أوراق. لا اتصالات. متوفر على iOS و Android.'
                : lang === 'fr'
                  ? "Acceptez les commandes, naviguez, scannez les codes-barres, collectez le cash, soyez payé. Pas de papier. Pas d'appels. Disponible sur iOS et Android."
                  : 'Accept orders, navigate, scan barcodes, collect cash, get paid. No paper. No phone calls. Available on iOS and Android.'}
            </p>

            <div className="dl-btns">
              <a href="#" className="dl-btn">
                <span className="dl-btn-icon">🍎</span>
                <div className="dl-btn-text">
                  <small>Download on the</small>
                  <b>App Store</b>
                </div>
              </a>
              <a href="#" className="dl-btn">
                <span className="dl-btn-icon">▶</span>
                <div className="dl-btn-text">
                  <small>Get it on</small>
                  <b>Google Play</b>
                </div>
              </a>
            </div>

            <div className="dl-qr">
              <div className="dl-qr-code">
                <svg viewBox="0 0 40 40" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" fill="#0A1E3A"/>
                  <rect x="3" y="3" width="12" height="12" rx="2" fill="none" stroke="#7CB342" strokeWidth="2"/>
                  <rect x="6" y="6" width="6" height="6" rx="1" fill="#7CB342"/>
                  <rect x="25" y="3" width="12" height="12" rx="2" fill="none" stroke="#7CB342" strokeWidth="2"/>
                  <rect x="28" y="6" width="6" height="6" rx="1" fill="#7CB342"/>
                  <rect x="3" y="25" width="12" height="12" rx="2" fill="none" stroke="#7CB342" strokeWidth="2"/>
                  <rect x="6" y="28" width="6" height="6" rx="1" fill="#7CB342"/>
                  {[
                    [20,4],[22,4],[24,4],[20,6],[24,6],[20,8],[22,8],
                    [20,20],[22,20],[24,20],[26,20],[28,20],[30,20],[32,20],
                    [20,22],[24,22],[28,22],[32,22],
                    [20,24],[22,24],[26,24],[30,24],
                    [20,26],[24,26],[28,26],[32,26],
                    [20,28],[22,28],[24,28],[26,28],[28,28],[30,28],[32,28],
                  ].map(([cx, cy], i) => (
                    <rect key={i} x={cx} y={cy} width="1.5" height="1.5" fill="#fff" opacity=".7"/>
                  ))}
                </svg>
              </div>
              <div className="dl-qr-info">
                <small>
                  {lang === 'ar' ? 'امسح للتحميل · أو أرسل الرابط لفريقك' : lang === 'fr' ? 'Scanner pour télécharger · Ou envoyer le lien par SMS' : 'Scan to download · Or text the link to your team'}
                </small>
                <div className="dl-qr-form">
                  <input
                    className="dl-qr-input"
                    type="tel"
                    placeholder="+216 26 531 407"
                    defaultValue="+216 26 531 407"
                  />
                  <button className="dl-qr-send">
                    {lang === 'ar' ? 'إرسال' : lang === 'fr' ? 'Envoyer' : 'Send link'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <PhoneMockup />

        </div>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section className="contact" id="contact">
        <div className="contact-inner">
          <div className="contact-copy">
            <div className="kicker">{t.contact.kicker}</div>
            <h2>{t.contact.title}</h2>
            <p>{t.contact.sub}</p>
            <div className="contact-info">
              {[
                { icon: '✉', label: t.contact.email, value: 'contact@traiqgo.tn' },
                { icon: '☎', label: t.contact.phone, value: '+216 26 531 407' },
                { icon: '⌂', label: t.contact.hq,    value: 'Tunis, Tunisie' },
              ].map(c => (
                <div className="contact-item" key={c.label}>
                  <span className="contact-icon">{c.icon}</span>
                  <div><small>{c.label}</small><b>{c.value}</b></div>
                </div>
              ))}
            </div>
          </div>
          <form
            className="contact-form"
            onSubmit={async e => {
              e.preventDefault()
              await submitRequest({
                name:    formData.name,
                email:   formData.email,
                phone:   formData.phone,
                company: formData.company || undefined,
                role:    formRole,
                volume:  formData.volume || undefined,
              })
            }}
            style={{ position: 'relative' }}
          >
            {formSuccess && (
              <div style={{ position: 'absolute', inset: 0, background: '#fff', borderRadius: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: '1.1rem', color: 'var(--navy-900)', fontWeight: 600 }}>
                <span style={{ display: 'grid', placeItems: 'center', width: 54, height: 54, borderRadius: 99, background: 'var(--green-500)', color: '#fff', fontSize: '1.5rem' }}>✓</span>
                {t.contact.form.success}
              </div>
            )}
            {formError && (
              <div className="form-err">{formError}</div>
            )}
            <div className="form-row">
              <label><span>{t.contact.form.name}</span><input className="form-input" type="text" placeholder="Aymen Ben Ali" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} /></label>
              <label><span>{t.contact.form.company}</span><input className="form-input" type="text" placeholder="Olive & Co." value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))} /></label>
            </div>
            <div className="form-row">
              <label><span>{t.contact.form.email}</span><input className="form-input" type="email" placeholder="vous@entreprise.tn" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} /></label>
              <label><span>{t.contact.form.phone}</span><input className="form-input" type="tel" placeholder="+216 26 531 407" required value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} /></label>
            </div>
            <label>
              <span>{t.contact.form.role_}</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, padding: 5, background: 'var(--navy-50)', borderRadius: 12 }}>
                {(['expediteur', 'livreur'] as Role[]).map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setFormRole(r)}
                    style={{ padding: '9px', borderRadius: 8, fontWeight: 600, fontSize: '.88rem', background: formRole === r ? 'var(--navy-900)' : 'transparent', color: formRole === r ? '#fff' : 'var(--navy-700)', border: 0, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s' }}
                  >
                     {r === 'expediteur' ? t.hero.role.expediteur : t.hero.role.livreur}
                  </button>
                ))}
              </div>
            </label>
            <label>
              <span>{t.contact.form.volume}</span>
              <select
                className="form-input"
                value={formData.volume}
                onChange={e => setFormData(p => ({ ...p, volume: e.target.value }))}
              >
                {t.contact.form.volumes.map(v => <option key={v}>{v}</option>)}
              </select>
            </label>
            <button
              className="btn btn-primary btn-lg btn-block"
              type="submit"
              disabled={formLoading}
              style={{ opacity: formLoading ? 0.7 : 1, cursor: formLoading ? 'wait' : 'pointer' }}
            >
              <span>{formLoading
                ? (lang === 'ar' ? 'إرسال…' : lang === 'fr' ? 'Envoi en cours…' : 'Sending…')
                : t.contact.form.submit}
              </span>
              {!formLoading && <span>{arrow}</span>}
            </button>
          </form>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/assets/logo.png" alt="TraiqGo" />
            <p>{t.footer.tagline}</p>
            <div className="socials">
              {['IG', 'FB', 'IN', 'X'].map(s => <a href="#" key={s}>{s}</a>)}
            </div>
          </div>
          {t.footer.cols.map(col => (
            <div className="footer-col" key={col.title}>
              <h5>{col.title}</h5>
              {col.links.map(l => <a href="#" key={l}>{l}</a>)}
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <span>{t.footer.copy}</span>
          <span className="footer-status">
            <span className="status-dot" /> {t.footer.status}
          </span>
        </div>
      </footer>

      {/* ═══ MODAL LOGIN ═══ */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <button className="modal-close" onClick={closeModal}>✕</button>
            <div className="modal-logo">
              <img src="/assets/logo.png" alt="TraiqGo" style={{ height: 44 }} />
            </div>
            <h3>{t.modal.title}</h3>
            <p className="modal-sub">{t.modal.sub}</p>
            <form className="modal-form" onSubmit={handleLogin}>
              {loginError && <div className="form-err">{loginError}</div>}
              <label>
                <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--navy-800)' }}>{t.modal.emailLabel}</span>
                <input className="form-input" type="email" placeholder="vous@entreprise.tn" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
              </label>
              <label>
                <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--navy-800)' }}>{t.modal.passLabel}</span>
                <div className="input-password-wrap">
                  <input
                    className="form-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="input-eye-btn"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
              <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={loginLoading}>
                {loginLoading ? t.modal.loading : t.modal.submit}
              </button>
            </form>
            <div className="modal-footer">
              <a href="/reset-password">{t.modal.forgot}</a>
              {' · '}
              <a href="/register">{t.modal.register}</a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
